import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out, object = logged in
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let mounted = true;
    // Only check session if there's a token — avoids noisy 401 on public pages.
    // This is an on-mount effect; `api` is a module singleton and setState
    // functions are stable, so no dependencies are required.
    const token = localStorage.getItem("albany_admin_token");
    if (!token) {
      if (mounted) {
        setUser(false);
        setChecked(true);
      }
      return () => {
        mounted = false;
      };
    }
    api
      .get("/auth/me")
      .then((r) => {
        if (mounted) setUser(r.data);
      })
      .catch(() => {
        if (mounted) setUser(false);
      })
      .finally(() => mounted && setChecked(true));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("albany_admin_token", data.token);
    setUser(data);
    return data;
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Log for observability but proceed with client-side cleanup regardless —
      // a network hiccup shouldn't leave a stale token in localStorage.
      // eslint-disable-next-line no-console
      console.warn("Server logout call failed, clearing client session anyway:", err?.message || err);
    }
    localStorage.removeItem("albany_admin_token");
    setUser(false);
  }

  return (
    <AuthCtx.Provider value={{ user, checked, login, logout, setUser }}>{children}</AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
